import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, Router } from '@angular/router';
import { ColumnModel, GridComponent } from '@syncfusion/ej2-angular-grids';
import { DataManager, Query } from '@syncfusion/ej2-data';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ConfigService } from 'src/app/services/config/config.service';
import { CookiesService } from 'src/app/services/cookies.service';
import { ToastSuccesModel } from '../../../models/toasts/toast-succes.model';
import { NotificationToastService } from '../../../services/notifications/notification-toast.service';
import { ChampPerso, ChampPersoPayload, IChampPersoViewModel } from '../models/champ-perso-models';

@Injectable({
  providedIn: 'root'
})
export class ChampsPersoService {
  sub: Subscription;
  private controllerName: string = "ChampsPersoPersonne";
  public champsLoaded$ =  new BehaviorSubject<any[]>([]);

  constructor(
    private http: HttpClient,
    private notifs: NotificationToastService,
    private router: Router,
    private route: ActivatedRoute,
    private configService: ConfigService,
    private cookiesService: CookiesService,) {
  }

  protected findParam(paramName: string, route: ActivatedRouteSnapshot): string {
    if (route == null) {
      return null;
    }
    if (route.paramMap.has(paramName)) {
      return route.paramMap.get(paramName);
    }
    return this.findParam(paramName, route.parent);
  }

  private get urlPersonne() {
    return `${this.configService.config.urlApi}/api/crm/ChampsPersoPersonne`
  }

  private get urlEntreprise() {
    return `${this.configService.config.urlApi}/api/crm/ChampsPersoEntreprise`
  }

  private get urlSession() {
    return `${this.configService.config.urlApi}/api/f/ChampsPersoSession`
  }

  private get urlInscription() {
    return `${this.configService.config.urlApi}/api/i/ChampsPersoInscription`
  }

  private get urlFormation() {
    return `${this.configService.config.urlApi}/api/f/ChampsPersoFormation`
  }

  private get urlFormateur() {
    return `${this.configService.config.urlApi}/api/crm/ChampsPersoFormateur`
  }

  selectController(controller: string) {
    this.controllerName = controller;
  }

  getAllChampsPersoPersonne(): Observable<IChampPersoViewModel[]> {
    return this.http.get<IChampPersoViewModel[]>(`${this.urlPersonne}/GetChamps`)
  }

  protected addChampPersoPersonne(champPerso: ChampPerso): Observable<string> {
    return this.http.post(`${this.urlPersonne}/AddChamp`, champPerso, { responseType: 'text' })
  }

  addChampPersonne(champ: ChampPersoPayload): Observable<string> {
    return this.addChampPersoPersonne(champ).pipe(tap(() => this.notifs.push(new ToastSuccesModel('Champ ajouté'))))
  }

  getChampPersonne(guid: any): Observable<IChampPersoViewModel> {
    return this.http.get<IChampPersoViewModel>(`${this.urlPersonne}/getByGuid/${guid}`);
  }

  navigateToListe() {
    this.router.navigateByUrl('parametres/personne')
  }

  getChampsPersosPersonneUtilises(): Observable<string[]> {
    return this.http.get<string[]>(`${this.urlPersonne}/GetChampsUtilises`)
  }

  getChampsPersosFormationUtilises(): Observable<string[]> {
    return this.http.get<string[]>(`${this.urlFormation}/GetChampsUtilises`)
  }

  getChampsPersosFormateurUtilises(): Observable<string[]> {
    return this.http.get<string[]>(`${this.urlFormateur}/GetChampsUtilises`)
  }

  public getChampsPersosEntrepriseUtilises(): Observable<string[]> {
    return this.http.get<string[]>(`${this.urlEntreprise}/GetChampsUtilises`)
  }

  public getChampsPersosSessionUtilises(): Observable<string[]> {
    return this.http.get<string[]>(`${this.urlSession}/GetChampsUtilises`)
  }

  public getChampsPersosInscriptionUtilises(): Observable<string[]> {
    return this.http.get<string[]>(`${this.urlInscription}/GetChampsUtilises`)
  }

  deleteChampPersonne(guid: string) {
    return this.http.delete(`${this.urlPersonne}/DeleteChamp/${guid}`).pipe(tap(() => this.notifs.push(new ToastSuccesModel('Champ supprimé'))))
  }

  updateChampPersonne(champ: ChampPersoPayload): Observable<string> {
    return this.http.post<string>(`${this.urlPersonne}/UpdateChamp`, champ).pipe(tap(() => this.notifs.push(new ToastSuccesModel('Champ mis à jour'))))
  }

  public getAllChampsEntreprise(): Observable<IChampPersoViewModel[]> {
    return this.http.get<IChampPersoViewModel[]>(`${this.urlEntreprise}/GetChamps`)
  }

  public addChampEntreprise(champ: ChampPersoPayload): Observable<string> {
    return this.http.post(`${this.urlEntreprise}/AddChamp`, champ, { responseType: 'text' }).pipe(tap(() => this.notifs.push(new ToastSuccesModel('Champ ajouté'))))
  }

  public updateChampEntreprise(champ: ChampPersoPayload): Observable<string> {
    return this.http.post<string>(`${this.urlEntreprise}/UpdateChamp`, champ).pipe(tap(() => this.notifs.push(new ToastSuccesModel('Champ mis à jour'))))
  }

  public deleteChampEntreprise(guid: string) {
    return this.http.delete(`${this.urlEntreprise}/DeleteChamp/${guid}`).pipe(tap(() => this.notifs.push(new ToastSuccesModel('Champ supprimé'))))
  }

  public getAllChampsSession(): Observable<IChampPersoViewModel[]> {
    return this.http.get<IChampPersoViewModel[]>(`${this.urlSession}/GetChamps`)
  }

  public addChampSession(champ: ChampPersoPayload): Observable<string> {
    return this.http.post(`${this.urlSession}/AddChamp`, champ, { responseType: 'text' }).pipe(tap(() => this.notifs.push(new ToastSuccesModel('Champ ajouté'))))
  }

  public updateChampSession(champ: ChampPersoPayload): Observable<string> {
    return this.http.post<string>(`${this.urlSession}/UpdateChamp`, champ).pipe(tap(() => this.notifs.push(new ToastSuccesModel('Champ mis à jour'))))
  }

  public deleteChampSession(guid: string) {
    return this.http.delete(`${this.urlSession}/DeleteChamp/${guid}`).pipe(tap(() => this.notifs.push(new ToastSuccesModel('Champ supprimé'))))
  }

  public getAllChampsInscrption(): Observable<IChampPersoViewModel[]> {
    return this.http.get<IChampPersoViewModel[]>(`${this.urlInscription}/GetChamps`)
  }

  public addChampInscription(champ: ChampPersoPayload): Observable<string> {
    return this.http.post(`${this.urlInscription}/AddChamp`, champ, { responseType: 'text' }).pipe(tap(() => this.notifs.push(new ToastSuccesModel('Champ ajouté'))))
  }

  public updateChampInscription(champ: ChampPersoPayload): Observable<string> {
    return this.http.post<string>(`${this.urlInscription}/UpdateChamp`, champ).pipe(tap(() => this.notifs.push(new ToastSuccesModel('Champ mis à jour'))))
  }

  public deleteChampInscription(guid: string) {
    return this.http.delete(`${this.urlInscription}/DeleteChamp/${guid}`).pipe(tap(() => this.notifs.push(new ToastSuccesModel('Champ supprimé'))))
  }

  public getCurrentColumnsNames(grid: GridComponent) {
    const result : string[] = [];
    grid.columns.forEach((col) => {
      result.push(col.field);
    });
    return result;
  }


  private sanitizeField(field: string): string {
    return field.replace(/\s+/g, '_').replace(/[^\w]/g, '');
  }

  public getColumnsForGridBis(champs: any[], cookieName: string): ColumnModel[] {
    const dynamicColumnsSet = new Set<string>();
    champs.forEach((champ) => {
      if (!dynamicColumnsSet.has(champ.Nom)) {
        dynamicColumnsSet.add(champ.Nom);
      }
    });

    const visibilityMap = this.cookiesService.getVisibilityMapFromCookie(cookieName);

    const dynamicColumns: ColumnModel[] = Array.from(dynamicColumnsSet).map(colName => {
      return {
        field: this.sanitizeField(colName),
        headerText: colName,
        textAlign: 'Left',
        visible: visibilityMap.has(colName) ? visibilityMap.get(colName) : false,
        allowFiltering: false,
        allowSorting: false,
        type: 'string',
      };
    });
    return dynamicColumns;
  }


  public getColumnsForGrid(data: DataManager, query: Query): Promise<any> {
    let result: any;
    return data.executeQuery(query).then((e: any) => {
      result = e.result;
      const dynamicColumnsSet = new Set<string>();

      result.forEach((record) => {
        record.ChampsAdditionnels.forEach((champ) => {
          if (!dynamicColumnsSet.has(champ.Nom)) {
            dynamicColumnsSet.add(champ.Nom);
          }
        });
      });

      const dynamicColumns = Array.from(dynamicColumnsSet).map(colName => {
        return {
          field: colName,
          headerText: colName,
          width: 150,
          textAlign: 'Left',
          visible: false,
        };
      });

      result = result.map(record => {
        record.ChampsAdditionnels.forEach((champ) => {
            record[champ.Nom] = champ.Valeur;
        });
        return record;
      });

      return { columns: dynamicColumns, data: result };
    }).catch(() => {
      if(result[0].ChampsAdditionnels === undefined){
        return { columns: [], data: result };
      }

      return null;
    });
  }

  public getAllChampsFormation(): Observable<IChampPersoViewModel[]> {
    return this.http.get<IChampPersoViewModel[]>(`${this.urlFormation}/GetChamps`)
  }

  public addChampFormation(champ: ChampPersoPayload): Observable<string> {
    return this.http.post(`${this.urlFormation}/AddChamp`, champ, {responseType: 'text'}).pipe(tap(() => this.notifs.push(new ToastSuccesModel('Champ ajouté'))))
  }

  public updateChampFormation(champ: ChampPersoPayload): Observable<string> {
    return this.http.post<string>(`${this.urlFormation}/UpdateChamp`, champ).pipe(tap(() => this.notifs.push(new ToastSuccesModel('Champ mis à jour'))))
  }

  public deleteChampFormation(guid: string) {
    return this.http.delete(`${this.urlFormation}/DeleteChamp/${guid}`).pipe(tap(() => this.notifs.push(new ToastSuccesModel('Champ supprimé'))))
  }

  public getAllChampsFormateur(): Observable<IChampPersoViewModel[]> {
    return this.http.get<IChampPersoViewModel[]>(`${this.urlFormateur}/GetChamps`)
  }

  public addChampFormateur(champ: ChampPersoPayload): Observable<string> {
    return this.http.post(`${this.urlFormateur}/AddChamp`, champ, {responseType: 'text'}).pipe(tap(() => this.notifs.push(new ToastSuccesModel('Champ ajouté'))))
  }

  public updateChampFormateur(champ: ChampPersoPayload): Observable<string> {
    return this.http.post<string>(`${this.urlFormateur}/UpdateChamp`, champ).pipe(tap(() => this.notifs.push(new ToastSuccesModel('Champ mis à jour'))))
  }

  public deleteChampFormateur(guid: string) {
    return this.http.delete(`${this.urlFormateur}/DeleteChamp/${guid}`).pipe(tap(() => this.notifs.push(new ToastSuccesModel('Champ supprimé'))))
  }

  public setChamps(champs: any[]) {
    this.champsLoaded$.next(champs);
  }
}
